import { useEffect, useState, useCallback, useRef, useContext } from 'react';
import PropTypes from 'prop-types';
import { FaClipboardList } from 'react-icons/fa';
import { getChecklistTemplates, importChecklistTemplate } from './checklistApi';
import ChecklistTemplateCard from './ChecklistTemplateCard';
import { Skeleton } from '../../components/Loaders/Skeleton/Skeleton';
import { UserContext } from '../../context/UserContext';
import UploadErrors from '../../components/UploadErrors';
import { validateFile } from '../../utils/file-validation';
import './ChecklistTemplatesList.scss';

// Templates are exported as JSON by the sibling "ייצוא" action.
const IMPORT_ACCEPT = { 'application/json': ['.json'] };
const IMPORT_MAX_SIZE = 5 * 1024 * 1024;

const LRU_STORAGE_KEY = 'checklist_templates_lru';
const MAX_LRU_ITEMS = 10;

// Helper functions for LRU
const getLRUData = () => {
    try {
        const data = localStorage.getItem(LRU_STORAGE_KEY);
        return data ? JSON.parse(data) : {};
    } catch (err) {
        console.error('Error reading LRU data:', err);
        return {};
    }
};

const updateLRU = (templateId) => {
    try {
        const lruData = getLRUData();
        lruData[templateId] = new Date().toISOString();

        // Keep only the most recent MAX_LRU_ITEMS
        const entries = Object.entries(lruData);
        if (entries.length > MAX_LRU_ITEMS) {
            entries.sort((a, b) => new Date(b[1]) - new Date(a[1]));
            const trimmedData = Object.fromEntries(entries.slice(0, MAX_LRU_ITEMS));
            localStorage.setItem(LRU_STORAGE_KEY, JSON.stringify(trimmedData));
        } else {
            localStorage.setItem(LRU_STORAGE_KEY, JSON.stringify(lruData));
        }
    } catch (err) {
        console.error('Error updating LRU data:', err);
    }
};

const sortTemplatesByLRU = (templates) => {
    const lruData = getLRUData();
    return [...templates].sort((a, b) => {
        const aTime = lruData[a._id] ? new Date(lruData[a._id]).getTime() : 0;
        const bTime = lruData[b._id] ? new Date(lruData[b._id]).getTime() : 0;
        return bTime - aTime; // Most recently used first
    });
};

function ChecklistTemplatesList({ onEdit, onDelete, refreshTrigger, onDataLoaded, onAddNew, canCreate }) {
    const { user, hospitals } = useContext(UserContext);
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [importError, setImportError] = useState(null);
    const [importing, setImporting] = useState(false);
    const fileInputRef = useRef(null);

    const fetchTemplates = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const templatesData = await getChecklistTemplates();
            const sortedTemplates = sortTemplatesByLRU(templatesData);
            setTemplates(sortedTemplates);
            if (onDataLoaded) {
                onDataLoaded(true);
            }
        } catch (err) {
            setError('אירעה שגיאה בטעינת תבניות הצ\'קליסטים');
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [onDataLoaded]);

    useEffect(() => {
        fetchTemplates();
    }, [fetchTemplates, refreshTrigger]);

    const handleImportClick = () => {
        if (importing) return;
        fileInputRef.current?.click();
    };

    const handleFileChange = async (event) => {
        const file = event.target.files?.[0];
        // Reset the input first so the same file can be re-picked after a fix.
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
        if (!file) return;

        setImportError(null);

        const rejection = validateFile(file, { accept: IMPORT_ACCEPT, maxSize: IMPORT_MAX_SIZE });
        if (rejection) {
            setImportError(`${rejection.message} יש לבחור קובץ JSON שיוצא מהמערכת.`);
            return;
        }

        setImporting(true);
        try {
            const fileContent = await file.text();
            const importData = JSON.parse(fileContent);

            // Get user's hospital and department
            let hospital = null;
            let department = null;

            if (user?.isSuperAdmin) {
                // For super admin, use the first available hospital and department
                if (hospitals.length > 0) {
                    hospital = hospitals[0]._id;
                    if (hospitals[0].departments?.length > 0) {
                        department = hospitals[0].departments[0]._id;
                    }
                }
            } else {
                // For regular users, use their first role's hospital and department
                if (user?.roles?.length > 0) {
                    hospital = user.roles[0].hospital;
                    department = user.roles[0].department;
                }
            }

            if (!hospital || !department) {
                setImportError('לא נמצאה מחלקה זמינה לייבוא.');
                return;
            }

            // Add hospital and department to import data
            const templateData = {
                ...importData,
                hospital,
                department
            };

            await importChecklistTemplate(templateData);
            await fetchTemplates();
        } catch (err) {
            console.error('Error importing template:', err);
            // A malformed JSON file and a rejected import fail very differently
            // — saying which saves the user from re-exporting a fine file.
            setImportError(
                err instanceof SyntaxError
                    ? 'הקובץ אינו קובץ JSON תקין.'
                    : (err?.serverError?.hebMessage || 'אירעה שגיאה בייבוא התבנית. ודא שהקובץ תקין.')
            );
        } finally {
            setImporting(false);
        }
    };

    // Rendered in both the populated list and the empty state — the import was
    // previously unreachable on a fresh install, which is exactly when it is
    // most useful.
    const importSection = user?.isSuperAdmin ? (
        <div className="import-section">
            <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept=".json,application/json"
                style={{ display: 'none' }}
            />
            <button onClick={handleImportClick} className="import-button" disabled={importing}>
                {importing ? 'מייבא...' : 'ייבוא'}
            </button>
            <UploadErrors
                messages={importError ? [importError] : []}
                onDismiss={() => setImportError(null)}
            />
        </div>
    ) : null;

    if (loading) {
        return (
            <div className="checklist-templates-list">
                <Skeleton>
                    <Skeleton.Block height="150px" />
                    <Skeleton.Block height="150px" />
                    <Skeleton.Block height="150px" />
                </Skeleton>
            </div>
        );
    }

    if (error) {
        return (
            <div className="checklist-templates-error">
                <p>{error}</p>
                <button onClick={fetchTemplates} className="retry-button">
                    נסה שנית
                </button>
            </div>
        );
    }

    if (templates.length === 0) {
        return (
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                minHeight: '50dvh'
            }}>
                <FaClipboardList size={60} style={{ marginBottom: '20px', color: '#0C3058' }} />
                <p style={{
                    fontSize: '1.5rem',
                    maxWidth: '600px',
                    marginBottom: '30px',
                    direction: 'rtl'
                }}>
                    כאן באפשרותך להעלות את רשימות (צ'קליסט) למעקב אחרי פעולות במטופלים.
                </p>
                {canCreate && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', alignItems: 'center', width: '100%', maxWidth: '300px' }}>
                        <button
                            onClick={onAddNew}
                            style={{
                                backgroundColor: 'white',
                                color: '#0C3058',
                                border: '1px solid #0C3058',
                                padding: '10px 20px',
                                borderRadius: '5px',
                                cursor: 'pointer',
                                boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
                                fontWeight: 'bold',
                                width: '100%'
                            }}>
                            צור רשימה
                        </button>
                    </div>
                )}
                {importSection && (
                    <div style={{ marginTop: '15px', display: 'flex', justifyContent: 'center', width: '100%' }}>
                        {importSection}
                    </div>
                )}
            </div>
        );
    }

    return (
        <>
            {importSection}
            <div className="checklist-templates-list">
                {templates.map(template => (
                    <ChecklistTemplateCard
                        key={template._id}
                        template={template}
                        onDelete={onDelete}
                        onAccess={updateLRU}
                    />
                ))}
            </div>
        </>
    );
}

ChecklistTemplatesList.propTypes = {
    onDelete: PropTypes.func.isRequired,
    refreshTrigger: PropTypes.number,
    onDataLoaded: PropTypes.func,
    onAddNew: PropTypes.func,
    canCreate: PropTypes.bool,
};

export default ChecklistTemplatesList;